import {ExpressionPath, Expressions} from '@packages/systems/wfdl';

import {EElement, type ExpressionElement} from '@packages/systems/wfdl-ast';

import {isOptimizationTreatmentElement} from '@packages/utilities/platform';
import {
  isSearchForm,
  isDynamoWrapper,
  isAnyCommerceDynListElement,
  isUserAccountSubscriptionListWrapper,
  isNavbarWrapper,
  isFormWrapper,
  isButton,
  isEmbed,
  isDropdownWrapper,
  isCommerceAddToCartWrapper,
  isCommerceCheckoutStripeElement,
  isWidget,
  isScript,
} from '@packages/utilities/elementTypeUtils';

import {isDynamoWrapperWithRandomSort} from '@packages/systems/dynamo/utils/DynamoElementUtils';

export const isNeverSearchIndexable = (
  element: ExpressionElement | null
): boolean => {
  if (!element) {
    return false;
  }

  return (
    isDynamoWrapperWithRandomSort(element) ||
    isOptimizationTreatmentElement(element)
  );
};

/**
 * Determines if the element is excluded from search by default.
 */
const isExcludedFromSearchByDefault = (element: ExpressionElement): boolean => {
  return (
    isSearchForm(element) ||
    isDynamoWrapper(element) ||
    isAnyCommerceDynListElement(element) ||
    isUserAccountSubscriptionListWrapper(element) ||
    isNavbarWrapper(element) ||
    isFormWrapper(element) ||
    isButton(element) ||
    isEmbed(element) ||
    isDropdownWrapper(element) ||
    isCommerceAddToCartWrapper(element) ||
    isCommerceCheckoutStripeElement(element) ||
    isWidget(element) ||
    isScript(element)
  );
};

/**
 * Determines if an element has an ancestor that is excluded from search
 */
export const hasAncestorExcludedFromSearch = (
  root: ExpressionElement,
  element: ExpressionElement
): boolean => {
  const rootElem = EElement(root);
  const parent = Expressions.getParent(rootElem, element.id);

  if (!root || !element || !parent) {
    return false;
  }

  const ancestorExcluded = Expressions.closestElement(
    rootElem,
    parent.id,
    isExcludedFromSearch
  );
  return Boolean(ancestorExcluded);
};

const SEARCH_EXCLUDE_PATH = [
  {in: 'Record', at: 'search'},
  {in: 'Record', at: 'exclude'},
] satisfies ExpressionPath;

/**
 * Determines if an element is excluded from search
 */
export const isExcludedFromSearch = (element: ExpressionElement): boolean => {
  if (isNeverSearchIndexable(element)) {
    return true;
  }

  const isExcludedByDefault = isExcludedFromSearchByDefault(element);
  const isExcluded = Expressions.getElementDataAt(
    element,
    SEARCH_EXCLUDE_PATH,
    'Boolean',
    isExcludedByDefault
  );
  return isExcluded;
};
